/* istanbul ignore file */
import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames/bind'
import GoogleMapReact from 'google-map-react'
import PropTypes from 'prop-types'
import { DEFAULT_ZOOM_LEVEL, US_CENTER_LAT_LNG } from '../../constants/map'
import Marker from '../marker'
import { setTriggeredMouseEventFlag } from '../../actions/assets'
import { getLatitude, getLongitude } from '../../util/mapUtils'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const GOOGLE_MAPS_LIBRARIES = ['places']

// settings for initial render before we locate, fitbounds etc.
const defaultSettings = {
  center: US_CENTER_LAT_LNG,
  zoom: DEFAULT_ZOOM_LEVEL,
}

const createMapOptions = maps => ({
  zoomControlOptions: {
    position: maps.ControlPosition.RIGHT_BOTTOM,
    style: maps.ZoomControlStyle.SMALL,
  },
  mapTypeControlOptions: {
    position: maps.ControlPosition.TOP_RIGHT,
  },
  mapTypeControl: true,
  fullscreenControl: false,
  streetViewControl: false,
  gestureHandling: 'greedy',
  clickableIcons: false,
})

const Map = ({
  markers,
  onMarkerClick,
  onBoundsChanged,
  bounds,
  assetsLoaded,
  showMap,
  googleMapsClientId,
  onGoogleMapsLoad,
  map,
  onMapClick,
  featureFlags,
  updateMapMarkerSelectedId,
  triggeredMouseEventFlag,
  setTriggeredMouseEventFlag,
}) => {
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false)

  const onGoogleApiLoaded = useCallback(
    mapInfo => {
      setGoogleMapsLoaded(true)
      onGoogleMapsLoad(mapInfo)
    },
    [onGoogleMapsLoad]
  )

  const createMarkers = useCallback(() => {
    const mapMarkers = markers.map((listing, i) => {
      const markerLatitude = getLatitude(listing)
      const markerLongitude = getLongitude(listing)
      return (
        markerLatitude &&
        markerLongitude && (
          <Marker
            key={`marker_${i}`}
            index={i}
            location={{ lat: markerLatitude, lng: markerLongitude }}
            onMarkerClick={onMarkerClick}
            listing={listing}
            map={map}
            lat={markerLatitude}
            lng={markerLongitude}
            bounds={bounds}
            updateSelectedId={updateMapMarkerSelectedId}
            triggeredMouseEventFlag={triggeredMouseEventFlag}
            setTriggeredMouseEventFlag={setTriggeredMouseEventFlag}
            featureFlags={featureFlags}
          />
        )
      )
    })
    return mapMarkers
  }, [
    markers,
    onMarkerClick,
    map,
    bounds,
    featureFlags,
    updateMapMarkerSelectedId,
    triggeredMouseEventFlag,
    setTriggeredMouseEventFlag,
  ])

  const mapMarkers = googleMapsLoaded ? createMarkers() : []

  return (
    <div className={cx('map-container', { 'hide-map': !showMap })}>
      <GoogleMapReact
        bootstrapURLKeys={{
          client: googleMapsClientId,
          language: 'en',
          version: 'weekly',
          libraries: GOOGLE_MAPS_LIBRARIES,
        }}
        defaultCenter={defaultSettings.center}
        defaultZoom={defaultSettings.zoom}
        options={createMapOptions}
        onChange={onBoundsChanged}
        onClick={onMapClick}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={onGoogleApiLoaded}
        resetBoundsOnResize={!map}
      >
        {assetsLoaded && mapMarkers}
      </GoogleMapReact>
    </div>
  )
}

Map.propTypes = {
  markers: PropTypes.array.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  onBoundsChanged: PropTypes.func.isRequired,
  bounds: PropTypes.object,
  assetsLoaded: PropTypes.bool.isRequired,
  showMap: PropTypes.bool.isRequired,
  googleMapsClientId: PropTypes.string,
  onGoogleMapsLoad: PropTypes.func,
  map: PropTypes.object,
  onMapClick: PropTypes.func.isRequired,
  featureFlags: PropTypes.object.isRequired,
  updateMapMarkerSelectedId: PropTypes.func.isRequired,
  triggeredMouseEventFlag: PropTypes.bool.isRequired,
  setTriggeredMouseEventFlag: PropTypes.func.isRequired,
}

const mapStateToProps = store => ({
  triggeredMouseEventFlag: store.assets?.triggeredMouseEventFlag,
})

const mapActions = {
  setTriggeredMouseEventFlag,
}

export default connect(mapStateToProps, mapActions)(Map)
